import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Layout, { Spinner } from '../components/Layout';
import { cases as casesApi, errorMessage, metadata as metadataApi } from '../utils/api';
import { formatDateTime, formatNumber, formatTime, isolate } from '../utils/format';
import { blankReportData, blankRow, renderReport } from '../utils/reportRender';
import ReportSheet from '../components/ReportSheet';

/**
 * صفحه‌ی تنظیم گزارش — جای فایل وردی که ادیتور بیرون از سامانه پر می‌کرد.
 *
 * فرم از روی قالب ساخته می‌شود، نه دستی: افزودن یک گزینه به کشویی‌ها فقط
 * تغییر `api/report_templates/implant.json` است و این فایل دست نمی‌خورد.
 */

/** فاصله‌ی بی‌حرکتی تا ذخیره‌ی خودکار. */
const AUTOSAVE_MS = 1500;

export default function ReportComposer({ item, onClose, onSaved }) {
  const [template, setTemplate] = useState(null);
  const [data, setData] = useState(null);
  const [serverText, setServerText] = useState('');
  const [locked, setLocked] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  // «تغییرِ ذخیره‌نشده» هم حالت است هم شرطِ ذخیره‌ی خودکار: تا وقتی چیزی
  // عوض نشده، بارگذاریِ اولیه نباید یک ذخیره‌ی بی‌مورد راه بیندازد.
  const [isDirty, setDirty] = useState(false);
  // «برگه» همان چیزی است که چاپ می‌شود؛ «متن» برای وقتی که ادیتور فقط
  // می‌خواهد سریع کلمات را ببیند.
  const [asSheet, setAsSheet] = useState(true);
  const timer = useRef(null);

  const readOnly = locked || item.status === 'technical_review';

  // ---- بارگذاری قالب و گزارشِ موجود
  useEffect(() => {
    let alive = true;

    Promise.all([metadataApi(), casesApi.getReport(item.id)])
      .then(([meta, report]) => {
        if (!alive) return;
        const loaded = meta.data.report_template;
        setTemplate(loaded);

        // ۲۰۴ یعنی هنوز گزارشی نوشته نشده.
        if (report.status === 204 || !report.data) {
          setData(blankReportData(loaded));
          return;
        }
        setData({ ...blankReportData(loaded), ...report.data.data });
        setServerText(report.data.rendered_text || '');
        setLocked(Boolean(report.data.is_locked));
        setSavedAt(report.data.updated_at);
      })
      .catch((err) => {
        if (alive) setLoadError(errorMessage(err, 'گزارش بارگذاری نشد.'));
      });

    return () => {
      alive = false;
    };
  }, [item.id]);

  // ---- ذخیره
  const persist = useCallback(
    async (payload) => {
      setSaving(true);
      try {
        const { data: saved } = await casesApi.saveReport(item.id, { data: payload });
        setServerText(saved.rendered_text || '');
        setSavedAt(saved.updated_at);
        setLocked(Boolean(saved.is_locked));
        setDirty(false);
        if (onSaved) onSaved(saved);
        return true;
      } catch (err) {
        toast.error(errorMessage(err, 'گزارش ذخیره نشد.'));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [item.id, onSaved]
  );

  // ذخیره‌ی خودکار پس از مکث. فرم بلند است و قطعی مرورگر نباید نیم‌ساعت
  // کار را ببرد.
  useEffect(() => {
    if (!isDirty || readOnly || !data) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(data), AUTOSAVE_MS);
    return () => clearTimeout(timer.current);
  }, [data, isDirty, persist, readOnly]);

  const change = useCallback((key, value) => {
    setDirty(true);
    setData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const changeRow = useCallback((blockKey, index, key, value) => {
    setDirty(true);
    setData((prev) => {
      const rows = [...(prev[blockKey] || [])];
      rows[index] = { ...rows[index], [key]: value };
      return { ...prev, [blockKey]: rows };
    });
  }, []);

  const addRow = useCallback((block) => {
    setDirty(true);
    setData((prev) => ({
      ...prev,
      [block.key]: [...(prev[block.key] || []), blankRow(block)],
    }));
  }, []);

  const removeRow = useCallback((block, index) => {
    setDirty(true);
    setData((prev) => ({
      ...prev,
      [block.key]: (prev[block.key] || []).filter((_, i) => i !== index),
    }));
  }, []);

  // پیش‌نمایش زنده. پس از هر ذخیره، متنِ سرور جایش را می‌گیرد — چیزی که
  // چاپ می‌شود همیشه آن است.
  const preview = useMemo(
    () => (isDirty || !serverText ? renderReport(template, data) : serverText),
    [template, data, serverText, isDirty]
  );

  const close = async () => {
    clearTimeout(timer.current);
    if (isDirty && !readOnly) await persist(data);
    onClose();
  };

  const backButton = (
    <button type="button" className="btn btn--ghost" onClick={close}>
      ↩️ بازگشت به میز
    </button>
  );

  if (loadError) {
    return (
      <Layout title="تنظیم گزارش" actions={backButton}>
        <p className="alert alert--danger">{loadError}</p>
      </Layout>
    );
  }

  if (!template || !data) {
    return (
      <Layout title="تنظیم گزارش" actions={backButton}>
        <Spinner />
      </Layout>
    );
  }

  return (
    <Layout
      title="تنظیم گزارش"
      subtitle={`${isolate(item.name)} — ${isolate(item.edit_type_name)}`}
      actions={backButton}
    >
      {readOnly && (
        <p className="alert alert--warning">
          {locked
            ? 'این گزارش پس از تأیید مسئول فنی قفل شده و دیگر ویرایش نمی‌شود.'
            : 'گزارش در انتظار تأیید مسئول فنی است و تا پاسخ او ویرایش نمی‌شود.'}
        </p>
      )}

      <div className="composer">
        <section className="composer__form">
          <PatientStrip item={item} />

          {template.blocks.map((block, index) => (
            <Block
              key={`${block.type}-${index}`}
              block={block}
              template={template}
              data={data}
              disabled={readOnly}
              onChange={change}
              onChangeRow={changeRow}
              onAddRow={addRow}
              onRemoveRow={removeRow}
            />
          ))}
        </section>

        <aside className="composer__preview">
          <div className="composer__preview-head">
            <h2>{asSheet ? 'برگه‌ی چاپی' : 'متن گزارش'}</h2>
            <SaveState saving={saving} savedAt={savedAt} dirty={isDirty} readOnly={readOnly} />
          </div>

          {asSheet ? (
            <div className="a5-frame">
              <ReportSheet item={item} text={preview} template={template} />
            </div>
          ) : (
            <PreviewSheet item={item} text={preview} direction={template.direction} />
          )}

          <div className="composer__preview-actions">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setAsSheet((value) => !value)}
            >
              {asSheet ? '📄 نمایش متنی' : '🖨️ نمایش برگه'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={isDirty || !preview}
              title={isDirty ? 'اول ذخیره کنید' : 'چاپ روی کاغذ خام A5'}
              onClick={() => window.print()}
            >
              🖨️ چاپ
            </button>
          </div>

          {!readOnly && (
            <button
              type="button"
              className="btn btn--primary btn--block"
              disabled={saving}
              onClick={() => persist(data)}
            >
              {saving ? 'در حال ذخیره…' : '💾 ذخیره‌ی گزارش'}
            </button>
          )}
        </aside>
      </div>
    </Layout>
  );
}

/** مشخصات بیمار — از پرونده می‌آید و دستی وارد نمی‌شود. */
function PatientStrip({ item }) {
  return (
    <dl className="composer__patient">
      <div>
        <dt>بیمار</dt>
        <dd><bdi>{item.name}</bdi></dd>
      </div>
      <div>
        <dt>پزشک ارجاع‌دهنده</dt>
        <dd><bdi>{item.doctor}</bdi></dd>
      </div>
      <div>
        <dt>شماره نسخه</dt>
        <dd className="num">{item.order_number}</dd>
      </div>
      <div>
        <dt>تاریخ پذیرش</dt>
        <dd>{formatDateTime(item.reception_time)}</dd>
      </div>
    </dl>
  );
}

function SaveState({ saving, savedAt, dirty, readOnly }) {
  if (readOnly) return <span className="composer__state">فقط خواندنی</span>;
  if (saving) return <span className="composer__state is-busy">در حال ذخیره…</span>;
  if (dirty) return <span className="composer__state is-dirty">ذخیره‌نشده</span>;
  if (savedAt) {
    return <span className="composer__state is-saved">ذخیره شد · {formatTime(savedAt)}</span>;
  }
  return <span className="composer__state">هنوز چیزی ذخیره نشده</span>;
}

/** برگه‌ی پیش‌نمایش — همان چیزی که روی کاغذ می‌رود. */
function PreviewSheet({ item, text, direction }) {
  return (
    <div className="sheet">
      <div className="sheet__head">
        <span><bdi>{item.name}</bdi></span>
        <span className="num">{item.order_number}</span>
      </div>
      <pre className="sheet__body" dir={direction || 'ltr'}>
        {text || 'هنوز چیزی انتخاب نشده است.'}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------- بندها

function Block({ block, template, data, disabled, onChange, onChangeRow, onAddRow, onRemoveRow }) {
  // متن ثابت فقط در پیش‌نمایش دیده می‌شود؛ در فرم چیزی برای پر کردن ندارد.
  if (block.type === 'paragraph' || block.type === 'spacer' || block.type === 'signature') {
    return null;
  }

  if (block.type === 'line') {
    const inputs = block.segments.filter((s) => s.kind !== 'static');
    if (!inputs.length) return null;
    return (
      <div className="composer__group">
        <div className="composer__row">
          {inputs.map((segment) => (
            <Field
              key={segment.key}
              segment={segment}
              template={template}
              value={data[segment.key]}
              disabled={disabled}
              onChange={(value) => onChange(segment.key, value)}
            />
          ))}
        </div>
      </div>
    );
  }

  if (block.type === 'rows') {
    const rows = data[block.key] || [];
    const inputs = block.segments.filter((s) => s.kind !== 'static');
    const canRemove = rows.length > (block.min_rows || 1);
    const canAdd = rows.length < (block.max_rows || 10);

    return (
      <div className="composer__group">
        <h3 className="composer__group-title">{block.label}</h3>

        {rows.map((row, index) => (
          // ردیف‌ها هویت پایداری ندارند؛ حذفِ ردیف میانی کل فهرست را
          // جابه‌جا می‌کند، پس اندیس همان کلیدی است که داریم.
          // eslint-disable-next-line react/no-array-index-key
          <div className="composer__row composer__row--numbered" key={index}>
            <span className="composer__row-num num">{index + 1}</span>
            {inputs.map((segment) => (
              <Field
                key={segment.key}
                segment={segment}
                template={template}
                value={row[segment.key]}
                disabled={disabled}
                onChange={(value) => onChangeRow(block.key, index, segment.key, value)}
              />
            ))}
            {!disabled && (
              <button
                type="button"
                className="composer__row-drop"
                title="حذف این ردیف"
                disabled={!canRemove}
                onClick={() => onRemoveRow(block, index)}
              >
                ✕
              </button>
            )}
          </div>
        ))}

        {!disabled && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={!canAdd}
            onClick={() => onAddRow(block)}
          >
            {/* نام خودِ بلوک روی دکمه می‌نشیند: «افزودن ناحیه» گویاتر از
                «افزودن ردیف» است، و با اضافه شدن بلوک تازه هم خودکار
                درست می‌ماند. */}
            ＋ افزودن {block.add_label || 'ردیف'}
          </button>
        )}
        {!canAdd && (
          <p className="field__hint">
            بیش از {formatNumber(block.max_rows)} {block.add_label || 'ردیف'} روی
            برگه‌ی A5 جا نمی‌شود.
          </p>
        )}
      </div>
    );
  }

  return null;
}

function Field({ segment, template, value, disabled, onChange }) {
  // جای‌نگهدارِ خودِ ورد («Choose an item.») کنار گذاشته می‌شود؛ فرم گزینه‌ی
  // خالیِ خودش را دارد و دوتا «انتخاب کنید» فقط گیج‌کننده است.
  const options = (template.option_sets[segment.options] || []).filter(
    (option) => !option.placeholder
  );

  return (
    <label className={`field field--report ${segment.kind === 'text' ? 'is-text' : ''}`}>
      <span>{segment.label}</span>
      {segment.kind === 'select' ? (
        <select dir="ltr" value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
          <option value="">— انتخاب کنید —</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          dir="ltr"
          value={value || ''}
          disabled={disabled}
          maxLength={segment.max_length || 200}
          placeholder={segment.placeholder || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}
